"use client";

import BFLLink from "@repo/shared-ui/BFLLink";
import BFLHoverBackdrop from "@repo/shared-ui/BFLHoverBackdrop";
import BFLImage from "@repo/shared-ui/BFLImage";
import { useState } from "react";
import Conditional from "@repo/shared-ui/Conditional";
import { handleHeaderPartnerClickDL } from "./HeaderAnalyticsFunction";
import { getAssetUrl } from "./assetUrl";

interface Partner {
  ctaInNewTab: string;
  ctaLink: string;
  image: string;
  imageAltText: string;
  label: string;
  nodeName?: string;
}

interface PartnerDataProps {
  partnerData: any;
}

const PartnerLinksMenu = ({ partnerData }: PartnerDataProps) => {
  const [isPartnerOpen, setIsPartnerOpen] = useState(false);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const setExpandedHeaderHoverColor = () => {
    document.documentElement.style.setProperty("--desktop-header-extra-bg", "#001326");
  };
  const clearExpandedHeaderHoverColor = () => {
    document.documentElement.style.removeProperty("--desktop-header-extra-bg");
  };

  // Handle different partner data structures
  let partners: Partner[] = [];

  // New partner data format - where partnerDetails is nested under partnerData.children
  if (partnerData?.children) {
    const partnerDetails = partnerData.children.find(
      (child: any) => child.nodeName === "partnerDetails"
    );
    if (partnerDetails?.children) {
      partners = partnerDetails.children.filter((item: any) => item.nodeName?.startsWith("item"));
    }
  }
  // Direct partnerDetails structure
  else if (partnerData?.nodeName === "partnerDetails" && Array.isArray(partnerData?.children)) {
    partners = partnerData.children.filter((item: any) => item.nodeName?.startsWith("item"));
  }
  // Legacy partner data format
  else if (partnerData?.[0]) {
    partners = Object.values(partnerData[0]);
  }

  const handlePartnerHover = (showOrHide: boolean) => {
    setIsPartnerOpen(showOrHide);
    if (showOrHide) {
      setExpandedHeaderHoverColor();
    } else {
      clearExpandedHeaderHoverColor();
    }
    if (!hasOpenedOnce) {
      setHasOpenedOnce(true); // Load image only once
    }
  };

  return (
    <nav
      className="hidden md:block group relative order-6 md:inline-block md:order-2"
      onMouseEnter={() => handlePartnerHover(true)}
      onMouseLeave={() => handlePartnerHover(false)}
      aria-label="Partner links"
    >
      <BFLHoverBackdrop zIndex={40} headerHeight={"var(--desktop-header-height,65px)"}>
        <button
          onClick={() => handleHeaderPartnerClickDL("Partners")}
          className={`my-0 hidden cursor-pointer flex-col items-center pt-[14px] pb-[8px] md:!flex md:gap-[5px] max-w-[58.14px]! ${isPartnerOpen ? "bg-[#001326]" : ""} hover:bg-[#001326]`}
          aria-label="Partners"
        >
          <i className="icomoon text-2xl before:content-['\e935']"></i>
          <span className="hidden items-center text-xs leading-[14px] text-white md:!inline-flex font-normal [font-family:Rubik,Mukta,BalooThambi2,sans-serif]">
            Partners{" "}
            <i className="icomoon ml-[2px] inline-flex h-[4px] w-[8px] shrink-0 items-center justify-center overflow-hidden p-0 leading-none transition-transform duration-300 group-hover:-rotate-180 before:inline-block before:h-[4px] before:w-[8px] before:overflow-hidden before:text-[8px] before:leading-[4px] before:content-['\e981']"></i>
          </span>
        </button>

        <div
          className={`absolute top-16.25 right-0 z-99 w-64 bg-[#000f1e] p-4 text-sm shadow-lg transition-all duration-300 ${isPartnerOpen ? "visible" : "invisible"}`}
        >
          {partners.map((partner, index) => (
            <div
              key={"partner-" + index}
              className="mb-2 flex items-center justify-start gap-3 overflow-y-auto rounded p-2 shadow-sm"
            >
              <Conditional if={hasOpenedOnce}>
                <BFLImage
                  src={getAssetUrl(partner.image)}
                  width={18}
                  height={18}
                  alt={partner.imageAltText}
                  priority={false}
                  loading="lazy"
                  unoptimized={true}
                />
              </Conditional>
              <BFLLink href={partner.ctaLink} openInNewTab={partner.ctaInNewTab === "true"}>
                {partner.label}
              </BFLLink>
            </div>
          ))}
        </div>
      </BFLHoverBackdrop>
    </nav>
  );
};

export default PartnerLinksMenu;
